import React from 'react';
import PropTypes from 'prop-types';
import { Alert } from '@mui/material';
import { borderRadius } from '../../Tokens/Scales';
import { font } from '../../Tokens/Typography';

/**
 * AppAlert — Design System feedback surface.
 *
 * Thin forwardRef wrapper over MUI Alert with token-driven radius/typography.
 * All MUI Alert props are supported. Use this instead of importing Alert
 * directly from @mui/material.
 *
 * @param {'error'|'warning'|'info'|'success'} severity  Colour + default icon (default 'info')
 * @param {React.ReactNode} icon     Overrides the severity icon; pass false to hide it
 * @param {React.ReactNode} action   Rendered on the trailing edge (buttons, pin toggle, …)
 * @param {Function}        onClose  Shows the built-in close button when provided
 */
const AppAlert = React.forwardRef(
  ({ severity = 'info', icon, action, onClose, children, sx, ...props }, ref) => (
    <Alert
      ref={ref}
      severity={severity}
      icon={icon}
      action={action}
      onClose={onClose}
      sx={[
        { borderRadius: `${borderRadius.sm}px`, ...font.bodyText },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...props}
    >
      {children}
    </Alert>
  ),
);

AppAlert.displayName = 'AppAlert';

AppAlert.propTypes = {
  severity: PropTypes.oneOf(['error', 'warning', 'info', 'success']),
  icon: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
  action: PropTypes.node,
  onClose: PropTypes.func,
  children: PropTypes.node,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default AppAlert;
